import { WSElement, WSText } from "@wingspanhq/fe-component-library";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import { ClientWorkFlowStatus } from "@wingspanhq/payments/dist/interfaces";
import React from "react";
import { useHistory } from "react-router-dom";
import { useBrowserPageTitle } from "../../../components/BrowserPageTitle/BrowserPageTitle";
import { useQueryPayables } from "../../../query/payments/queries";
import { WSQueries } from "../../../query/WSQuery";
import {
  PayablesFilters,
  PayablesStatusFilter,
  PayablesTable,
  usePayablesQuery
} from "../../components/NewPayablesTable/PayablesTable";
import { PayableActionBlock } from "../../components/PayableActionBlock/PayableActionBlock";
import { useDefaultCurrency } from "../../../shared/hooks/useDefaultCurrency";
import { API_QUERY_PAYABLES_DUAL_STAGE_TO_PREAPPROVE } from "../../utils/payables/apiQueries";
import { useDualStagePreapproveAll } from "../../utils/payables/approveAll";

const DEFAULT_FILTERS: PayablesFilters = {
  status: [PayablesStatusFilter.ToApprove],
  custom: {
    "client.workflowStatus": {
      notIn: [
        ClientWorkFlowStatus.PreApproved,
        ClientWorkFlowStatus.Approved,
        ClientWorkFlowStatus.PaymentInitiated,
        ClientWorkFlowStatus.Funded
      ]
    }
  }
};

export const PayablesDualStageToPreApprove: React.FC = () => {
  useBrowserPageTitle("Payables To Pre-Approve");
  const history = useHistory();
  const defaultCurrency = useDefaultCurrency();
  const draftPayablesQuery = usePayablesQuery({
    status: [PayablesStatusFilter.Draft]
  });

  const queryPayablesToPreApprove = useQueryPayables({
    ...API_QUERY_PAYABLES_DUAL_STAGE_TO_PREAPPROVE,
    page: {
      size: 1
    }
  });

  const { act: preApproveAll, isLoading: preApproveAllIsLoading } =
    useDualStagePreapproveAll();

  return (
    <>
      <WSText.Heading1>To Pre-Approve</WSText.Heading1>
      <WSText color="gray600" mb="2XL">
        All payables and requests that are not yet scheduled to be paid.
      </WSText>

      <WSElement mb="2XL">
        <WSQueries
          queries={{ draftPayablesQuery, queryPayablesToPreApprove }}
          renderErrors={() => null}
        >
          {({
            draftPayablesQuery: {
              data: { summary: draftPayablesSummary }
            },
            queryPayablesToPreApprove: {
              data: { summary: toPreApprovePayablesSummary }
            }
          }) => (
            <>
              {toPreApprovePayablesSummary.listSize > 1 && (
                <PayableActionBlock
                  mb="XL"
                  icon="dollar-circle"
                  rightButton={{
                    children: `Pre-approve ${toPreApprovePayablesSummary.listSize} payments`,
                    loading: preApproveAllIsLoading,
                    onClick: preApproveAll
                  }}
                  onClick={preApproveAll}
                >
                  <WSText color="gray500" mb="XS">
                    <WSText as="b" inline color="gray700">
                      Pending pre-approval:
                    </WSText>{" "}
                    {toPreApprovePayablesSummary.listSize} payables totaling{" "}
                    {formatMoney(
                      toPreApprovePayablesSummary.totalValue,
                      defaultCurrency
                    )}
                    .
                  </WSText>
                </PayableActionBlock>
              )}

              {draftPayablesSummary.listSize > 0 && (
                <PayableActionBlock
                  onClick={() => {
                    history.push("/member/invoices/payables/draft");
                  }}
                  icon="alert-circle"
                  contentButton={{
                    children: "Manage draft payables"
                  }}
                >
                  <WSText color="gray500">
                    <WSText as="b" color="gray700">
                      You have {draftPayablesSummary.listSize} draft payable
                      {draftPayablesSummary.listSize > 1 ? "s" : ""}.
                    </WSText>
                  </WSText>
                </PayableActionBlock>
              )}
            </>
          )}
        </WSQueries>
      </WSElement>

      <PayablesTable
        bulkActions={["preapprove", "delete"]}
        visibleFilters={{
          payee: true,
          collaboratorReviewStatus: true,
          openedAtRange: true
        }}
        basePath="/member/invoices/payables/to-preapprove"
        defaultFilters={DEFAULT_FILTERS}
        showHeaders
        showInvoiceNumber
        showDateRequested
        showDueDate
        emptyState={{
          firstTime:
            "Every requested, scheduled, and paid payable will display here.",
          standart:
            "You're all caught up! Nothing is waiting for your pre-approval."
        }}
      />
    </>
  );
};
